import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AmplitudeService } from '@inyova/inyova-shared';
import { GrowAccount, InyovaGrowAccountStatus } from '@inyova/models';

import { GrowCoupon, GrowCouponStatus, GrowCouponType, GrowProject, GrowProjectStage, GrowProjectStatus } from '../../models';
import { getGrowPaymentCouponBadge, getGrowProjectBadgeDetails } from '../../service';

export enum TabList {
  DETAILS,
  RISKS,
  OPPORTUNITIES,
  IMPACT
}

@Component({
  selector: 'app-grow-project-detail',
  templateUrl: './grow-project-detail.component.html',
  styleUrls: ['./grow-project-detail.component.scss'],
  standalone: false
})
export class GrowProjectDetailComponent implements OnInit {
  @Input() growAccount: GrowAccount;
  @Input() project: GrowProject;
  @Input() projectTotalInvestments: number;
  @Input() language: string;

  @Output() activeTabChange: EventEmitter<number> = new EventEmitter<number>();

  activeTab = TabList.DETAILS;
  switcherTabs = [
    { id: TabList.DETAILS, value: 'GROW.project.tabs.details' },
    { id: TabList.RISKS, value: 'GROW.project.tabs.risks' },
    { id: TabList.OPPORTUNITIES, value: 'GROW.project.tabs.opportunities' },
    { id: TabList.IMPACT, value: 'GROW.project.tabs.impact' }
  ];

  projectTransactionsDetails: {
    totalExpectedInterest: number;
    totalInterestCount: number;
    alreadyReceivedInterest: number;
    alreadyReceivedInterestCount: number;
    upcoming: GrowCoupon[];
    next: GrowCoupon[];
    past: GrowCoupon[];
  };

  projectStatus = GrowProjectStatus;
  projectStage = GrowProjectStage;
  tabList = TabList;

  showInfo = {
    progress: false,
    status: false,
    stage: false
  };

  showMore = {
    upcomingPayments: false,
    pastPayments: false
  };

  projectProgress = null;
  isProjectInactive = false;
  accountStatusList: typeof InyovaGrowAccountStatus = InyovaGrowAccountStatus;

  constructor(private amplitudeService: AmplitudeService) {}

  ngOnInit() {
    const secured = this.project.volume_secured_from_customers ?? 0;
    const target = this.project.inyova_volume ?? 0;

    this.projectProgress = target > 0 ? Math.floor((secured / target) * 100) : 0;

    this.isProjectInactive = [GrowProjectStage.CLOSED, GrowProjectStage.CANCELLED].includes(this.project.stage);

    if (this.growAccount.step_status !== InyovaGrowAccountStatus.FUNDED || this.project.account_specific.transactions.length === 0) {
      return;
    }
    this.projectTransactionsDetails = this.getTransactionsDetails(this.project.account_specific.transactions);
  }

  getBadge(status: GrowProjectStatus) {
    return getGrowProjectBadgeDetails(status);
  }

  getCouponBadge(status: GrowCouponStatus) {
    return getGrowPaymentCouponBadge(status);
  }

  getTransactionsDetails(transactions: GrowCoupon[]): {
    totalExpectedInterest: number;
    totalInterestCount: number;
    alreadyReceivedInterest: number;
    alreadyReceivedInterestCount: number;
    upcoming: GrowCoupon[];
    next: GrowCoupon[];
    past: GrowCoupon[];
  } {
    // group coupons paid vs overdue - pending
    const { totalExpectedInterest, totalInterestCount, alreadyReceivedInterest, alreadyReceivedInterestCount, future, past } = [...transactions]
      .reverse()
      .reduce(
        (acc, item) => {
          if (item.type === GrowCouponType.INTEREST) {
            acc.totalExpectedInterest = acc.totalExpectedInterest + Number(item.amount);
            acc.totalInterestCount = acc.totalInterestCount + 1;

            if (item.status === 'paid') {
              acc.alreadyReceivedInterest = acc.alreadyReceivedInterest + Number(item.amount);
              acc.alreadyReceivedInterestCount = acc.alreadyReceivedInterestCount + 1;
            }
          }
          acc[item.status === 'overdue' || item.status === 'pending' ? 'future' : 'past'].push(item);
          return acc;
        },
        { totalExpectedInterest: 0, totalInterestCount: 0, alreadyReceivedInterest: 0, alreadyReceivedInterestCount: 0, future: [], past: [] }
      );

    return {
      totalExpectedInterest,
      totalInterestCount,
      alreadyReceivedInterest,
      alreadyReceivedInterestCount,
      upcoming: future.slice(0, -3),
      next: future.slice(-3),
      past
    };
  }

  switchTabs(tabIndex: number) {
    if (tabIndex === this.activeTab) {
      return;
    }
    this.activeTab = tabIndex;
    this.activeTabChange.emit(tabIndex);
  }

  toggleInfo(item: 'progress' | 'status' | 'stage') {
    this.showInfo[item] = !this.showInfo[item];
    this.amplitudeService.trackEvent('Tooltip Info Consulted', {
      tooltip_name: item,
      screen: 'Grow project detail',
      consultation_method: 'click'
    });
  }

  trackTooltip(name: string) {
    this.amplitudeService.trackEvent('Tooltip Info Consulted', {
      tooltip_name: name,
      screen: 'Grow project detail',
      consultation_method: 'click'
    });
  }

  getInvestmentBadge(status: GrowProjectStatus): { type: string; key: string } {
    return getGrowProjectBadgeDetails(status);
  }
}
